// Coordinates the three trusted prerequisites for a local acceptance lane.
// Visible start and SDK bootstrap are intentionally independent, and neither
// is proof that the game world exists yet. The deterministic run may begin
// only after Game has reported a next-turn, playable world snapshot.

export function inspectLocalVariantWorld({ state, spawner, camera, player, controls } = {}) {
  const missing = [];
  if (state?.phase !== 'playing') missing.push(`phase=${state?.phase ?? 'missing'}`);
  if (!spawner) missing.push('spawner');
  if (!camera) missing.push('camera');
  if (!player?.weapon) missing.push('player.weapon');
  // The first-run Controls sheet deliberately returns the game to MENU until
  // the player closes it. Do not stage behind that visible, trusted UI.
  if (controls?.open) missing.push('controls-open');
  return Object.freeze({ ready: missing.length === 0, missing: Object.freeze(missing) });
}

// A signed local acceptance URL is not an alternate scoring mode. Its one
// permitted submission is the run that the local lane completed through a real
// enemy death. This guard keeps an incomplete lane's natural zero-score death
// out of the official SDK while leaving normal/public game deaths untouched.
export function canSubmitLocalVariantScore(lane, runCounter) {
  return !!lane
    && lane.runCounter === runCounter
    && lane.state === 'completed'
    && lane.enemyDeathSeen === true
    && Number.isInteger(lane.killsAfter)
    && lane.killsAfter > lane.killsBefore;
}

export function createLocalVariantGate(runOnce) {
  if (typeof runOnce !== 'function') throw new TypeError('runOnce must be a function');
  let trustedStartSeen = false;
  let sessionReady = false;
  let worldReady = false;
  let started = false;

  const maybeRun = () => {
    if (started || !trustedStartSeen || !sessionReady || !worldReady) return false;
    started = true;
    runOnce();
    return true;
  };

  return Object.freeze({
    markTrustedStart() { trustedStartSeen = true; return maybeRun(); },
    markSessionReady() { sessionReady = true; return maybeRun(); },
    markWorldReady() { worldReady = true; return maybeRun(); },
    maybeRun,
    get trustedStartSeen() { return trustedStartSeen; },
    get sessionReady() { return sessionReady; },
    get worldReady() { return worldReady; },
    get started() { return started; },
  });
}
